import { Expose, Type } from 'class-transformer';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnUserSettings } from '@lib/api/entities';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

import { SnChannel } from '../models';
import { SnChatUser } from './../models/chat-user.model';

@Injectable({ providedIn: 'root' })
export class SnMembersService {
  @Expose()
  @Type(() => SnUserSettings)
  public settings: SnUserSettings;

  @Expose()
  public isExternal: boolean;

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _dialogService: SnDialogService,
    private readonly _toastrService: ToastrService,
  ) {}

  public inviteUser(sid: string, userIds: string[]): Observable<SnChannel> {
    return this._httpClient
      .post(`/api/communications/chats/channels/${sid}/invite/`, { users: userIds })
      .pipe(
        map((response: Object) => new SnChannel(response)),
        tap({
          next: () => this._toastrService.success(t('communication.chat.messages.inviteSent')),
          error: () => this._toastrService.error(t('communication.chat.errors.sendInvite')),
        }),
      );
  }

  public inviteExtUser(sid: string, data: any[]): Observable<SnChannel> {
    return this._httpClient
      .post(`/api/communications/chats/channels-organization/${sid}/invite/`, {
        externalUsers: [data],
      })
      .pipe(
        map((response: Object) => new SnChannel(response)),
        tap({
          next: () => this._toastrService.success(t('communication.chat.messages.inviteSent')),
          error: () => this._toastrService.error(t('communication.chat.errors.sendInvite')),
        }),
      );
  }

  public removeUser(sid: string, userId: string): Observable<SnChannel> {
    return this._dialogService
      .confirm({
        message: t('communication.chat.confirmations.deleteUserFromChannel'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._removeRawUser(sid, userId)),
        map((response: Object) => new SnChannel(response)),
      );
  }

  public joinToChannel(sid: string, type: string): Observable<SnChannel> {
    return this._httpClient
      .post(`/api/communications/chats/channels-${type}/${sid}/join/`, null)
      .pipe(
        map(
          (response: any) =>
            new SnChannel({
              ...response,
              users: response.users.map((id) => ({ id })),
            }),
        ),
        tap({
          next: () => this._toastrService.success(t('communication.chat.messages.joinedSuccessfully')),
          error: (err: HttpErrorResponse) => {
            if (err?.error?.publicity?.toLowerCase()?.includes('channel is private')) {
              this._toastrService.error(t('communication.chat.errors.joinChannelPrivate'));
            } else {
              this._toastrService.error(t('communication.chat.errors.joinChannel'));
            }
          },
        }),
      );
  }

  public leaveFromChannel(sid: string, type: string): Observable<SnChannel> {
    return this._httpClient
      .post(`/api/communications/chats/channels-${type}/${sid}/leave/`, null)
      .pipe(map((response: Object) => new SnChannel(response)));
  }

  public fetchMemberList(
    sid: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnChatUser>> {
    return this._httpClient
      .get(`/api/communications/chats/channels/${sid}/members/`, {
        params,
      })
      .pipe(
        map((response: any) => ({
          count: response.count,
          totalPages: response.totalPages,
          results: response.results.map((members: any) => new SnChatUser(members)),
        })),
      );
  }

  public updateMember(chatChannelSid: string, userId: string, data: any): Observable<SnChatUser> {
    return this._httpClient
      .patch(`/api/communications/chats/channels/${chatChannelSid}/members/${userId}/`, data)
      .pipe(
        map((response: any) => new SnChatUser(response)),
        tap({
          next: () => this._toastrService.success(t('communication.chat.messages.memberUpdatedSuccessfully')),
          error: () =>
            this._toastrService.error(t('communication.chat.errors.updateMember')),
        }),
      );
  }

  private _removeRawUser(sid: string, userId: string): Observable<SnChannel> {
    return this._httpClient
      .post(`/api/communications/chats/channels/${sid}/remove_user/`, { removeUser: userId })
      .pipe(map((response: Object) => new SnChannel(response)));
  }
}
